import { useEffect, useState } from "react";
import { Layout } from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { useDispatch, useSelector } from "react-redux";
import { acceptJob } from "../notifications/notificationsAPI";
import { GetAppointmentDetailsAPI } from "./appointmentAPI";
import { clearAppointmentData } from "./appointmentSlice";
import { AlertDialog } from "../../components/ui/AlertDialog";
import { Textarea } from "../../components/ui/textarea";
import { Skeleton } from "../../components/ui/skeleton";
import { useToast } from "../../hooks/use-toast";
import {
  MapPin,
  Calendar,
  Clock,
  Phone,
  User,
  FileText,
  Hash,
  File,
} from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

const formatTime = (timeString) => {
  if (!timeString || typeof timeString !== "string") return "";
  const today = new Date().toISOString().split("T")[0];
  const fullDateTimeString = `${today}T${timeString}`;
  const date = new Date(fullDateTimeString);
  if (isNaN(date)) return "Invalid Time";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

function AppointmentJobPage({ appointmentId, onNavigate }) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const intl = useIntl();

  const { currentAppointment: appointment, loading: appointmentLoading } =
    useSelector((state) => state.appointment);

  const [status, setStatus] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    if (appointmentId) {
      dispatch(clearAppointmentData());
      dispatch(GetAppointmentDetailsAPI(appointmentId));
    }
  }, [appointmentId, dispatch]);

  useEffect(() => {
    if (appointment?.technicianStatusId) {
      setStatus(appointment.technicianStatusId);
    }
  }, [appointment]);

  const handleJobAction = (technicianStatusId, reason = null) => {
    const payload = {
      AppointmentId: appointmentId,
      TechnicianStatusId: technicianStatusId,
      RejectReason: reason,
    };
    setActionLoading(true);
    dispatch(acceptJob(payload))
      .then(() => {
        setStatus(technicianStatusId);
        toast({
          title: intl.formatMessage({ id: "Appointment.Toast.Success.Accept" }),
          description:
            technicianStatusId === 19
              ? intl.formatMessage({ id: "Appointment.Toast.Success.Accept" })
              : intl.formatMessage({ id: "Appointment.Toast.Success.Reject" }),
          variant: "success",
        });
      })
      .catch((err) => {
        toast({
          title: intl.formatMessage({ id: "Appointment.Toast.Error.Title" }),
          description: intl.formatMessage(
            { id: "Appointment.Toast.Error.Desc" },
            { error: err.message || err }
          ),
          variant: "error",
        });
      })
      .finally(() => {
        setActionLoading(false);
        setIsRejectDialogOpen(false);
        setRejectReason("");
      });
  };

  const handleConfirmReject = () => {
    if (rejectReason.trim() === "") {
      toast({
        title: intl.formatMessage({
          id: "Appointment.Toast.ValidationError.Title",
        }),
        description: intl.formatMessage({
          id: "Appointment.Toast.ValidationError.Desc",
        }),
        variant: "destructive",
      });
      return;
    }
    handleJobAction(20, rejectReason);
  };

  const {
    appointmentNumber = "-",
    clientDetails,
    startDate,
    startTime,
    notes: description,
  } = appointment || {};
  const maskPhone = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, ""); // remove non-digits
    const visible = cleaned.slice(-4);
    return cleaned
      .replace(new RegExp(`\\d(?=\\d{4})`, "g"), "*")
      .replace(/(\*{3})(\*+)(\d{4})/, "$1-$2-$3");
  };
  const clientName = clientDetails?.name || clientDetails?.companyName || "-";
  const clientPhone = clientDetails?.phone || "Not available";
  const appointmentAddress = clientDetails?.shippingAddress
    ? [
        clientDetails.shippingAddress.address1,
        clientDetails.shippingAddress.address2,
        clientDetails.shippingAddress.city,
        clientDetails.shippingAddress.province,
        clientDetails.shippingAddress.country,
        clientDetails.shippingAddress.zip,
      ]
        .filter(Boolean)
        .join(", ")
    : "Not available";

  const JobDetailsSkeleton = () => (
    <div className="p-4">
      <Card>
        <CardContent className="!p-4 space-y-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  );

  if (appointmentLoading) {
    return (
      <Layout
        title={intl.formatMessage({ id: "Appointment.Loading" })}
        onNavigate={onNavigate}
      >
        <JobDetailsSkeleton />
      </Layout>
    );
  }

  return (
    <Layout
      title={intl.formatMessage(
        { id: "Appointment.Title" },
        { number: appointmentNumber }
      )}
      onNavigate={onNavigate}
    >
      <div className="space-y-6">
        <Card>
          <CardContent className="!p-4 space-y-4">
            <div className="flex items-center gap-3">
              <Hash className="h-5 w-5 text-gray-600" />
              <span className="text-lg font-bold">{appointmentNumber}</span>
            </div>
            <div className="flex items-center gap-3">
              <File className="h-5 w-5 text-gray-600" />
              <span className="font-semibold">{appointment?.woNumber}</span>
            </div>
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-blue-600" />
              <span className="font-semibold">{clientName}</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-blue-600" />
              <span>{maskPhone(clientPhone.replace(/Cell\s+/, ""))}</span>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-green-600 mt-1" />
              <p className="flex-1">{appointmentAddress}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 bg-gray-50 dark:bg-muted rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {startDate ? new Date(startDate).toLocaleDateString() : "-"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-500 dark:text-orange-400" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {formatTime(startTime)}
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-gray-400 mt-1" />
              <p className="flex-1 italic text-gray-600">
                {description || (
                  <FormattedMessage id="Appointment.NoDescription" />
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-center">
          {status === 18 && (
            <div className="flex space-x-4">
              <Button
                onClick={() => handleJobAction(19)}
                className="bg-green-600 hover:bg-green-700"
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <FormattedMessage id="Appointment.Accepting" />
                ) : (
                  <FormattedMessage id="Appointment.AcceptJob" />
                )}
              </Button>
              <Button
                onClick={() => setIsRejectDialogOpen(true)}
                variant="destructive"
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <FormattedMessage id="Appointment.Rejecting" />
                ) : (
                  <FormattedMessage id="Appointment.RejectJob" />
                )}
              </Button>
            </div>
          )}
          {status === 19 && (
            <p className="text-lg font-semibold text-green-600">
              <FormattedMessage id="Appointment.Accepted" />
            </p>
          )}
          {status === 20 && (
            <p className="text-lg font-semibold text-red-600">
              <FormattedMessage id="Appointment.Rejected" />
            </p>
          )}
        </div>

        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => onNavigate("dashboard")}
            className="mt-4"
          >
            <FormattedMessage id="Appointment.BackToDashboard" />
          </Button>
        </div>
      </div>

      <AlertDialog
        open={isRejectDialogOpen}
        onOpenChange={setIsRejectDialogOpen}
        title={<FormattedMessage id="Appointment.RejectDialog.Title" />}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setIsRejectDialogOpen(false)}
            >
              <FormattedMessage id="Appointment.RejectDialog.Cancel" />
            </Button>
            <Button
              onClick={handleConfirmReject}
              disabled={actionLoading || !rejectReason.trim()}
              variant="destructive"
            >
              {actionLoading ? (
                <FormattedMessage id="Appointment.RejectDialog.Submitting" />
              ) : (
                <FormattedMessage id="Appointment.RejectDialog.Confirm" />
              )}
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 mb-4">
          <FormattedMessage id="Appointment.RejectDialog.Info" />
        </p>
        <Textarea
          placeholder={intl.formatMessage({
            id: "Appointment.RejectDialog.Placeholder",
          })}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          className="min-h-[100px]"
        />
      </AlertDialog>
    </Layout>
  );
}

export default AppointmentJobPage;
